import React from 'react'
import { motion } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiSettings, FiCheck, FiX, FiAlertTriangle, FiInfo, FiExternalLink } = FiIcons

const DebugPanel = () => {
  const envVars = [
    {
      name: 'VITE_NCB_BASE_URL',
      description: 'Base URL for NoCodeBackend API',
      required: true,
      value: import.meta.env.VITE_NCB_BASE_URL,
      example: 'https://openapi.nocodebackend.com'
    },
    {
      name: 'VITE_NCB_INSTANCE_ID',
      description: 'NoCodeBackend Instance ID',
      required: true,
      value: import.meta.env.VITE_NCB_INSTANCE_ID,
      example: '53830_github_cloudflare_pages'
    },
    {
      name: 'VITE_NCB_API_KEY',
      description: 'NoCodeBackend API Key',
      required: true,
      value: import.meta.env.VITE_NCB_API_KEY,
      example: 'your-ncb-api-key-here'
    },
    {
      name: 'VITE_DROPBOX_APP_KEY',
      description: 'Dropbox App Key (Client ID)',
      required: false,
      value: import.meta.env.VITE_DROPBOX_APP_KEY,
      example: 'your-dropbox-app-key'
    }
  ]

  const serverOnlyVars = [
    {
      name: 'DROPBOX_APP_SECRET',
      description: 'Dropbox App Secret (Server-side only)',
      note: 'Should be configured in Cloudflare Pages Functions environment'
    },
    {
      name: 'DROPBOX_REFRESH_TOKEN',
      description: 'Dropbox Refresh Token (Server-side only)',
      note: 'Should be configured in Cloudflare Pages Functions environment'
    }
  ]

  const getStatusIcon = (value) => {
    return value ? (
      <SafeIcon icon={FiCheck} className="h-5 w-5 text-green-500" />
    ) : (
      <SafeIcon icon={FiX} className="h-5 w-5 text-red-500" />
    )
  }

  const getStatusText = (value, required) => {
    if (value) return 'Configured'
    return required ? 'Missing (Required)' : 'Not Set (Optional)'
  }

  const getStatusColor = (value, required) => {
    if (value) return 'text-green-600'
    return required ? 'text-red-600' : 'text-yellow-600'
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center mb-6">
        <SafeIcon icon={FiSettings} className="h-6 w-6 text-purple-600 mr-3" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Debug Panel</h2>
          <p className="text-sm text-gray-600">Environment configuration and system status</p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Environment Variables */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiInfo} className="h-4 w-4 mr-2" />
            Frontend Environment Variables
          </h3>
          
          <div className="space-y-3">
            {envVars.map((envVar) => (
              <motion.div
                key={envVar.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gray-50 rounded-lg p-4 border border-gray-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      {getStatusIcon(envVar.value)}
                      <code className="ml-2 text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                        {envVar.name}
                      </code>
                      {envVar.required && (
                        <span className="ml-2 text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                          Required
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-2">{envVar.description}</p>
                    
                    <div className="text-xs text-gray-500">
                      <span className="font-medium">Example:</span> <code>{envVar.example}</code>
                    </div>
                  </div>
                  
                  <div className="ml-4 text-right">
                    <div className={`text-sm font-medium ${getStatusColor(envVar.value, envVar.required)}`}>
                      {getStatusText(envVar.value, envVar.required)}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Server-Only Variables */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiAlertTriangle} className="h-4 w-4 mr-2 text-yellow-500" />
            Server-Side Environment Variables
          </h3>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800">
              These variables should be configured in Cloudflare Pages environment settings, not in your code.
              They are used by Pages Functions for secure API operations.
            </p>
          </div>
          
          <div className="space-y-3">
            {serverOnlyVars.map((envVar) => (
              <div key={envVar.name} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-center mb-2">
                  <SafeIcon icon={FiSettings} className="h-4 w-4 text-gray-400 mr-2" />
                  <code className="text-sm font-mono text-gray-900 bg-gray-100 px-2 py-1 rounded">
                    {envVar.name}
                  </code>
                </div>
                <p className="text-sm text-gray-600 mb-1">{envVar.description}</p>
                <p className="text-xs text-gray-500">{envVar.note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Configuration Instructions */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center">
            <SafeIcon icon={FiExternalLink} className="h-4 w-4 mr-2" />
            Configuration Instructions
          </h3>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Cloudflare Pages Setup</h4>
            <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
              <li>Go to your Cloudflare Pages dashboard</li>
              <li>Select your project</li>
              <li>Navigate to Settings → Environment Variables</li>
              <li>Add the missing environment variables listed above</li>
              <li>Redeploy your application</li>
            </ol>
          </div>
        </div>

        {/* System Information */}
        <div>
          <h3 className="text-md font-medium text-gray-900 mb-4">System Information</h3>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Node Environment:</span>
              <span className="font-mono">{import.meta.env.MODE}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Build Time:</span>
              <span className="font-mono">{new Date().toISOString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">User Agent:</span>
              <span className="font-mono text-xs truncate max-w-xs" title={navigator.userAgent}>
                {navigator.userAgent}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DebugPanel